import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import logo from "../assets/logo.png";

const Header = ({ isAuthenticated, onLogout }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeLink, setActiveLink] = useState("");
  const [isScrolled, setIsScrolled] = useState(false);
  const [isAdminMenuOpen, setIsAdminMenuOpen] = useState(false);
  const [isMobileAdminOpen, setIsMobileAdminOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const currentDate = new Date().toLocaleDateString("en-US", {
    timeZone: "Asia/Kathmandu",
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  // Set activeLink based on current pathname
  useEffect(() => {
    const pathToLink = {
      "/": "Home",
      "/courses": "Courses",
      "/career": "Career",
      "/about": "About Us",
      "/blogs": "Blogs",
      "/send-inquiry": "Contact Us",
      "/inquiry": "Inquiry",
      "/reports": "Reports",
      "/edit": "Edit",
      "/login": "Login",
    };

    const currentLink = pathToLink[location.pathname] || "";
    setActiveLink(currentLink);
  }, [location.pathname]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (isMenuOpen) {
      setIsMobileAdminOpen(false);
    }
  };

  const toggleMobileAdminMenu = () => {
    setIsMobileAdminOpen(!isMobileAdminOpen);
  };

  const handleExploreCourses = () => {
    navigate("/join-class");
  };

  const handleLinkClick = (linkName, path) => {
    setActiveLink(linkName);
    setIsMenuOpen(false);
    setIsAdminMenuOpen(false);
    setIsMobileAdminOpen(false);
    navigate(path);
  };

  const handleSendInquiry = () => {
    setActiveLink("Contact Us");
    setIsMenuOpen(false);
    navigate("/send-inquiry");
  };

  const handleLogoutClick = () => {
    onLogout();
    setActiveLink("");
    setIsMenuOpen(false);
    setIsMobileAdminOpen(false);
    navigate("/");
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <style>{`
        .nav-link {
          position: relative;
          transition: color 0.3s ease;
        }
        .nav-link::after {
          content: '';
          position: absolute;
          width: 0;
          height: 2px;
          bottom: -2px;
          left: 0;
          background-color: #2563eb;
          transition: width 0.3s ease;
        }
        .nav-link:hover::after {
          width: 100%;
        }
        .nav-link.active::after {
          width: 100%;
        }

        .btn-arrow {
          transition: transform 0.3s ease;
        }
        .btn:hover .btn-arrow {
          transform: translateX(4px);
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.95; }
        }
        .banner-pulse {
          animation: pulse 3s ease-in-out infinite;
          position: relative;
          overflow: hidden;
        }

        @keyframes slideOffer {
          0%, 100% { transform: translateX(0) scale(1); }
          50% { transform: translateX(8px) scale(1.02); }
          25% { transform: translateX(-8px) scale(1.02); }
        }
        .offer-slide {
          animation: slideOffer 2s ease-in-out infinite;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .hamburger-line {
          transition: all 0.3s ease;
        }
        .hamburger-open .line-top {
          transform: rotate(45deg) translate(5px, 5px);
        }
        .hamburger-open .line-middle {
          opacity: 0;
        }
        .hamburger-open .line-bottom {
          transform: rotate(-45deg) translate(5px, -5px);
        }

        .admin-button {
          background: linear-gradient(135deg, #7c3aed, #4c1d95);
        }
        .admin-button:hover {
          background: linear-gradient(135deg, #6d28d9, #3b0764);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }

        /* Admin Dropdown Styles */
        .admin-dropdown {
          position: relative;
        }
        .admin-dropdown-menu {
          position: absolute;
          top: 100%;
          right: 0;
          background: white;
          border-radius: 0.5rem;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          min-width: 160px;
          z-index: 50;
          opacity: 0;
          visibility: hidden;
          transform: translateY(10px);
          transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s;
        }
        .admin-dropdown:hover .admin-dropdown-menu {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }
        .admin-dropdown-item {
          display: block;
          padding: 0.75rem 1rem;
          color: #374151;
          font-size: 0.875rem;
          font-weight: 500;
          transition: background 0.2s ease, color 0.2s ease;
        }
        .admin-dropdown-item:hover {
          background: #f3f4f6;
          color: #2563eb;
        }
        .admin-dropdown-item.active {
          color: #2563eb;
          background: #eff6ff;
        }

        /* Mobile Admin Submenu Styles */
        .mobile-admin-menu {
          background: #1f2937;
          border-radius: 0.5rem;
          margin-top: 0.5rem;
          margin-left: 1rem;
          overflow: hidden;
          transition: max-height 0.3s ease, opacity 0.3s ease;
        }
        .mobile-admin-menu-item {
          display: block;
          padding: 0.75rem 1rem;
          color: white;
          font-size: 0.875rem;
          font-weight: 500;
          transition: background 0.2s ease, color 0.2s ease;
        }
        .mobile-admin-menu-item:hover {
          background: #374151;
          color: #93c5fd;
        }
        .mobile-admin-menu-item.active {
          color: #93c5fd;
          background: #4b5563;
        }

        /* Custom style for mobile menu font size */
        .mobile-menu-link {
          font-size: 1rem;
        }
      `}</style>
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-white/80 backdrop-blur-md shadow-lg"
            : "bg-white shadow-md"
        }`}
      >
        {/* Promotional Banner */}
        <div
          className="bg-blue-950 text-white flex justify-between items-center px-4 py-1 text-sm md:text-lg banner-pulse"
          style={{
            boxShadow: "inset 0 0 15px rgba(0, 0, 0, 0.3)",
          }}
        >
          <div className="flex flex-col">
            <span className="text-xs md:text-sm text-gray-200 mb-1">
              {currentDate}
            </span>
            <div className="offer-slide" style={{ textShadow: "0 1px 2px rgba(0, 0, 0, 0.4)" }}>
              <svg
                className="w-5 h-5 text-white"
                fill="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
              </svg>
              <span className="font-bold">Limited Time Offer!</span>
              <span className="hidden sm:inline">
                🚀 Join Data Science & MERN today! 🎯 30% Off
              </span>
              <span className="sm:hidden">🎯 30% Off</span>
            </div>
          </div>
          <button
            onClick={handleExploreCourses}
            className="btn font-semibold text-blue-900 bg-yellow-400 hover:bg-yellow-500 rounded-lg px-2 py-1.5 text-base sm:px-2 flex items-center shadow-md hover:shadow-lg transition-all duration-300"
            style={{
              boxShadow: "0 0 10px rgba(251, 191, 36, 0.5)",
            }}
          >
            <span>Join Now</span>
          </button>
        </div>

        {/* Main Navigation */}
        <nav className="flex justify-between items-center px-4 py-2 max-w-7xl mx-auto">
          {/* Logo */}
          <div className="flex items-center space-x-4">
            <Link to="/">
              <img
                src={logo}
                alt="Virtueista IT logo"
                className="w-24 md:w-28 filter"
              />
            </Link>
          </div>

          {/* Hamburger Menu Button (Mobile) */}
          <button
            className="md:hidden focus:outline-none p-2 rounded-full hover:bg-gray-100 transition-colors duration-300"
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            <svg
              className={`w-8 h-8 text-gray-700 ${isMenuOpen ? "hamburger-open" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                className="hamburger-line line-top"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d={isMenuOpen ? "M6 18L18 6" : "M4 6h16"}
              />
              <path
                className="hamburger-line line-middle"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 12h16"
              />
              <path
                className="hamburger-line line-bottom"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d={isMenuOpen ? "M6 6l12 12" : "M4 18h16"}
              />
            </svg>
          </button>

          {/* Navigation Links (Desktop) */}
          <div className="hidden md:flex md:items-center md:justify-center md:flex-1 md:space-x-8">
            {[
              { name: "Home", path: "/" },
              { name: "Courses", path: "/courses" },
              { name: "Career", path: "/career" },
              { name: "About Us", path: "/about" },
              { name: "Blogs", path: "/blogs" },
            ].map((link) => (
              <a
                key={link.name}
                onClick={() => handleLinkClick(link.name, link.path)}
                className={`nav-link text-lg text-gray-700 hover:text-blue-600 tracking-wide transition-all duration-300 cursor-pointer ${
                  activeLink === link.name ? "active text-blue-600" : ""
                }`}
              >
                {link.name}
              </a>
            ))}
            {isAuthenticated && (
              <div
                className="admin-dropdown"
                onMouseEnter={() => setIsAdminMenuOpen(true)}
                onMouseLeave={() => setIsAdminMenuOpen(false)}
              >
                <a
                  className="nav-link text-lg text-gray-700 hover:text-blue-600 tracking-wide transition-all duration-300 cursor-pointer flex items-center"
                >
                  Admin
                  <svg
                    className="w-4 h-4 ml-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </a>
                <div className="admin-dropdown-menu">
                  {[
                    { name: "Inquiry", path: "/inquiry" },
                    { name: "Reports", path: "/reports" },
                    { name: "Edit", path: "/edit" },
                  ].map((link) => (
                    <a
                      key={link.name}
                      onClick={() => handleLinkClick(link.name, link.path)}
                      className={`admin-dropdown-item ${
                        activeLink === link.name ? "active" : ""
                      }`}
                    >
                      {link.name}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons (Desktop) */}
          <div className="hidden md:flex md:items-center md:space-x-3">
            <button
              onClick={handleSendInquiry}
              className="btn bg-blue-500 hover:bg-blue-600 border hover:shadow-lg shadow-lg border-blue-700 text-white rounded-lg px-3 py-2 flex items-center"
            >
              <span>Contact Us</span>
            </button>
            {!isAuthenticated ? (
              <button
                onClick={() => handleLinkClick("Login", "/login")}
                className="btn bg-blue-500 hover:bg-blue-600 border hover:shadow-lg shadow-lg border-blue-700 text-white rounded-lg px-3 py-2 flex items-center space-x-2"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
                <span>Login</span>
              </button>
            ) : (
              <button
                onClick={handleLogoutClick}
                className="btn bg-red-500 hover:bg-red-600 border hover:shadow-lg shadow-lg border-red-700 text-white rounded-lg px-3 py-2 flex items-center space-x-2"
              >
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5-5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
                </svg>
                <span>Logout</span>
              </button>
            )}
          </div>
        </nav>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-gray-800 p-4">
            <ul className="space-y-4">
              {[
                { name: "Home", path: "/" },
                { name: "Courses", path: "/courses" },
                { name: "Career", path: "/career" },
                { name: "About Us", path: "/about" },
                { name: "Blogs", path: "/blogs" },
                { name: "Contact Us", path: "/send-inquiry" },
              ].map((link) => (
                <li key={link.name}>
                  <button
                    onClick={() => handleLinkClick(link.name, link.path)}
                    className={`text-left mobile-menu-link font-medium text-white hover:text-yellow-400 transition-colors duration-200 w-full ${
                      activeLink === link.name
                        ? "underline underline-offset-4 decoration-yellow-400 decoration-2"
                        : ""
                    }`}
                  >
                    {link.name}
                  </button>
                </li>
              ))}
              {isAuthenticated && (
                <li>
                  <button
                    onClick={toggleMobileAdminMenu}
                    className={`text-left mobile-menu-link font-medium text-white hover:text-yellow-400 transition-colors duration-200 w-full flex items-center ${
                      activeLink === "Inquiry" ||
                      activeLink === "Reports" ||
                      activeLink === "Edit"
                        ? "underline underline-offset-4 decoration-yellow-400 decoration-2"
                        : ""
                    }`}
                  >
                    Admin
                    <svg
                      className={`w-4 h-4 ml-2 transform ${
                        isMobileAdminOpen ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {isMobileAdminOpen && (
                    <ul className="mobile-admin-menu">
                      {[
                        { name: "Inquiry", path: "/inquiry" },
                        { name: "Reports", path: "/reports" },
                        { name: "Edit", path: "/edit" },
                      ].map((link) => (
                        <li key={link.name}>
                          <button
                            onClick={() => handleLinkClick(link.name, link.path)}
                            className={`mobile-admin-menu-item w-full text-left ${
                              activeLink === link.name ? "active" : ""
                            }`}
                          >
                            {link.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              )}
              <li>
                <button
                  onClick={
                    isAuthenticated
                      ? handleLogoutClick
                      : () => handleLinkClick("Login", "/login")
                  }
                  className={`text-left mobile-menu-link font-medium text-white hover:text-yellow-400 transition-colors duration-200 w-full ${
                    activeLink === (isAuthenticated ? "Logout" : "Login")
                      ? "underline underline-offset-4 decoration-yellow-400 decoration-2"
                      : ""
                  }`}
                >
                  {isAuthenticated ? "Logout" : "Login"}
                </button>
              </li>
            </ul>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
